import actionTypes from '../actions/actionTypes'
import slideTypes from 'helpers/enums/slideTypes'
import sessionTypes from 'helpers/enums/sessionTypes'
import jwtDecode from 'jwt-decode'

const defaultState = {
	slides: {
	},
	current_slide: null,
	device_uid: null,
	headers: null,
	sessionType: null,
	current_status: {},
	wereMicPermissionsRequired: false,
	isStudentNameAutofilled: false,
	shouldShowImmersiveReader: false,
}

function initializeSession(state, action) {
	return {
		...state,
		device_uid: 's' + action.session.device_uid,
		current_slide: action.slide_uid,
		headers: action.session.headers,
		sessionType: action.session.sessionType,
		session_uid: action.session.session_uid,
		presentation_uid: action.session.presentation_uid,
		pincode: action.session.pincode,
		join_token: action.session.join_token,
	}
}

function initializeSlide(state, slideUid, slideState) {
	const newSlides = {...state.slides}
	newSlides[slideUid] = {
		...newSlides[slideUid],
		...slideState,
	}
	return {
		...state,
		slides: newSlides,
	}
}

function initializePoll(state, action) {

	const buildAnswers = () => action.answers || []

	let slideState
	if (!state.slides[action.slide_uid]) {
		slideState = {
			answers: buildAnswers(),
			title: action.questionData.title,
			plainTextTitle: action.questionData.plainTextTitle,
			options: action.questionData.options.map((option, index) => ({
				...option,
				colorIndex: index,
			})),
			reference_media: action.questionData.reference_media,
			slideIndex: action.slide.slideId,
			canMultiSelect: action.questionData.canMultiSelect,
			shouldBlockMedia: action.slide.shouldBlockMedia,
			canEdit: action.session.canEdit,
			timer: action.slide.timer,
			hasChanged: true,
			question_key: action.questionData.question_key,
			userDataPath: action.slide.userDataPath,
			isSubmissionRequired: action.slide.isSubmissionRequired,
			shouldShowImmersiveReader: action.slide.shouldShowImmersiveReader,
			type: slideTypes.SLIDE_TYPE_ID_POLL,
		}
	} else {
		slideState = {
			answers: buildAnswers(),
			shouldBlockMedia: action.slide.shouldBlockMedia,
			timer: action.slide.timer,
			userDataPath: action.slide.userDataPath,
		}
	}
	return initializeSession(initializeSlide(state, action.slide_uid, slideState), action)
}

function initializeQuiz(state, action) {

	const buildQuestions = () => action.questionData.questions.map(question => {
		const answer_ids = []
		return {
			...question,
			options: question.options.map(option => {
				answer_ids.push(option.key)
				return {
					...option,
					colorIndex: 'default',
				}
			}),
			selected_answers: action.answers.filter(answer => answer_ids.includes(answer)),
		}
	})

	let slideState
	if (!state.slides[action.slide_uid]) {
		slideState = {
			questions: buildQuestions(),
			currentQuestionIndex: 0,
			transitionDirection: 0,
			isComplete: false,
			slideIndex: action.slide.slideId,
			shouldBlockMedia: action.slide.shouldBlockMedia,
			canEdit: action.session.canEdit,
			timer: action.slide.timer,
			hasChanged: true,
			subtype: action.slide.subtype,
			userDataPath: action.slide.userDataPath,
			editsCount: 0,
			shouldShowImmersiveReader: action.slide.shouldShowImmersiveReader,
			isSubmissionRequired: action.slide.isSubmissionRequired,
			type: slideTypes.SLIDE_TYPE_ID_QUIZ,
		}
	} else {
		slideState = {
			questions: buildQuestions(),
			currentQuestionIndex: 0,
			transitionDirection: 0,
			isComplete: false,
			shouldBlockMedia: action.slide.shouldBlockMedia,
			timer: action.slide.timer,
			userDataPath: action.slide.userDataPath,
		}
	}
	return initializeSession(initializeSlide(state, action.slide_uid, slideState), action)
}

function initializeOeq(state, action) {

	const buildAnswers = () => [{
		...action.questionData.options[0],
		text: action.answers[0] || '',
	}]

	let slideState
	if (!state.slides[action.slide_uid]) {
		slideState = {
			answers: buildAnswers(),
			title: action.questionData.title,
			plainTextTitle: action.questionData.plainTextTitle,
			reference_media: action.questionData.reference_media,
			slideIndex: action.slide.slideId,
			shouldBlockMedia: action.slide.shouldBlockMedia,
			isSubmissionRequired: action.slide.isSubmissionRequired,
			timer: action.slide.timer,
			canEdit: action.session.canEdit,
			hasChanged: true,
			question_key: action.questionData.question_key,
			userDataPath: action.slide.userDataPath,
			editsCount: 0,
			studentRecordingAudioEnabled: action.slide.studentRecordingAudioEnabled,
			recordedAudio: action.audio.url,
			shouldShowImmersiveReader: action.slide.shouldShowImmersiveReader,
			type: slideTypes.SLIDE_TYPE_ID_OPEN_ENDED_QUESTION,
			audioClientUid: action.audio.uid,
			audioServerUid: action.audio.uid,
		}
	} else {
		slideState = {
			answers: buildAnswers(),
			shouldBlockMedia: action.slide.shouldBlockMedia,
			timer: action.slide.timer,
			userDataPath: action.slide.userDataPath,
			audioClientUid: action.audio.uid,
			audioServerUid: action.audio.uid,
			recordedAudio: action.audio.url,
		}
	}

	return initializeSession(initializeSlide(state, action.slide_uid, slideState), action)
}

function initializeDrawIt(state, action) {

	let slideState
	if (!state.slides[action.slide_uid]) {
		slideState = {
			title: action.questionData.title,
			reference_media: action.questionData.reference_media,
			backgroundImage: action.questionData.backgroundImage,
			slideIndex: action.slide.slideId,
			canEdit: action.session.canEdit,
			hasChanged: true,
			question_key: action.questionData.question_key,
			userDataPath: action.slide.userDataPath,
			editsCount: 0,
			shouldShowImmersiveReader: action.slide.shouldShowImmersiveReader,
			isSubmissionRequired: action.slide.isSubmissionRequired,
			type: slideTypes.SLIDE_TYPE_ID_DRAW_IT,
		}
	}
	slideState = {
		...slideState,
		shouldBlockMedia: action.slide.shouldBlockMedia,
		timer: action.slide.timer,
		answers: action.actions,
		initialData: action.actions,
		isPristine: true,
		shouldDisableGoogleImages: action.slide.shouldDisableGoogleImages,
	}
	return initializeSession(initializeSlide(state, action.slide_uid, slideState), action)
}

function initializeMatchingPairs(state, action) {
	let slideState
	if (!state.slides[action.slide_uid]) {
		slideState = {
			timer: action.slide.timer,
			slideIndex: action.slide.slideId,
			hasChanged: true,
			shouldShowImmersiveReader: action.slide.shouldShowImmersiveReader,
			type: slideTypes.SLIDE_TYPE_ID_MATCHING_PAIRS,
		}
	} else {
		slideState = {
			timer: action.slide.timer,
		}
	}
	return initializeSession(initializeSlide(state, action.slide_uid, slideState), action)
}

function initializeLead(state, action) {
	let initialName = ''
	let initialOptionalName = ''
	const isSessionPreview = action.session.isSessionPreview
	if (state.slides[action.slide_uid]) {
		initialName = state.slides[action.slide_uid].name
		initialOptionalName = state.slides[action.slide_uid].optionalName
	} else if (isSessionPreview) {
		initialName = action.slide.complementaryLeadData.firstField
		initialOptionalName = action.slide.complementaryLeadData.secondField
	}

	const slideState = {
		name: initialName,
		optionalName: initialOptionalName,
		lastSubmittedName: action.name,
		lastSubmittedOptionalName: action.optionalName,
		complementaryLeadData: action.slide.complementaryLeadData,
		appDataLeadName: action.slide.appDataLeadName,
		lastNicknameUserLoggedSigned: action.lastNicknameUserLoggedSigned,
		slideType: slideTypes.SLIDE_TYPE_ID_LEAD,
		slideIndex: action.slide.slideId,
		isSubmitting: false,
		isValid: true,
		email: '',
		imageSrc: '',
	}
	const newState = {
		...state,
		isFromLTI: action.session.isFromLTI,
	}

	const resultState = initializeSession(initializeSlide(newState, action.slide_uid, slideState), action)
	if (isSessionPreview) {
		return handleLeadSubmit(resultState, {
			slide_uid: action.slide_uid,
			sessionType: action.session.sessionType,
			newJwt: action.session.headers.jwt,
		})
	} else {
		return resultState
	}
}

function updatePollAnswer(state, action) {
	const slides = {...state.slides}
	slides[action.slide_uid] = {
		...slides[action.slide_uid],
		answers: action.answers,
		hasChanged: true,
	}
	return {
		...state,
		slides,
	}
}

function updateDrawItAnswer(state, action) {
	const slides = {...state.slides}
	slides[action.slide_uid] = {
		...slides[action.slide_uid],
		answers: action.answers,
		isPristine: false,
		hasChanged: true,
	}
	return {
		...state,
		slides,
	}
}

function updateQuizAnswer(state, action) {
	const slides = {...state.slides}
	const slide = slides[action.slide_uid]
	const questions = slide.questions
	const questionIndex = action.questionIndex
	const question = questions[questionIndex]
	const updatedQuestion = {
		...question,
		selected_answers: action.answers,
	}
	let editsCount = slides[action.slide_uid].editsCount
	if (!slides[action.slide_uid].hasChanged) {
		editsCount++
	}
	slides[action.slide_uid] = {
		...slide,
		questions: [...questions.slice(0, questionIndex), updatedQuestion, ...questions.slice(questionIndex + 1, questions.length)],
		hasChanged: true,
		editsCount,
	}
	return {
		...state,
		slides,
	}
}

function updateOeqAnswer(state, action) {
	const slides = {...state.slides}
	let editsCount = slides[action.slide_uid].editsCount
	if (!slides[action.slide_uid].hasChanged) {
		editsCount++
	}
	slides[action.slide_uid] = {
		...slides[action.slide_uid],
		answers: [{
			...slides[action.slide_uid].answers[0],
			text: action.answer,
		}],
		hasChanged: true,
		editsCount,
	}
	return {
		...state,
		slides,
	}
}

function handlePollActivitySubmission(state, action) {
	const slides = {...state.slides}
	slides[action.slide_uid] = {
		...slides[action.slide_uid],
		hasChanged: false,
	}
	return {
		...state,
		slides,
	}
}

function handleOeqActivitySubmission(state, action) {
	const slides = {...state.slides}
	const currentSlide = slides[action.slide_uid]

	const has_answers = Object.keys(currentSlide.answers).findIndex(
		key => currentSlide.answers[key].text !== ''
	)

	if (has_answers > -1
		|| action.type === actionTypes.STUDENT_SUBMIT_OEQ_CONFIRMED
		|| currentSlide.audioClientUid
	) {
		slides[action.slide_uid] = {
			...slides[action.slide_uid],
			hasChanged: false,
		}
		return {
			...state,
			slides,
		}
	} else {
		return state
	}
}

function handleActivityEnded(state, action) {
	const newState = {...state}
	const slides = {...state.slides}
	slides[action.slide_uid] = {
		...slides[action.slide_uid],
		timer: action.slide.timer,
	}
	newState.slides = slides
	if (state.current_slide === action.slide_uid) {
		newState.current_slide = null
		newState.current_status = {}
	}
	return newState
}

function handleStudentStatusUpdated(state, action) {
	if (JSON.stringify(action.status_data) === JSON.stringify(state.current_status)) {
		return state
	}
	return {
		...state,
		current_status: action.status_data,
	}
}

function handleStudentTimerUpdated(state, action) {
	const slideUid = action.slide_uid
	const slide = state.slides[slideUid]
	const timer = slide.timer
	if (!action.timer || action.timer.length === 0 || JSON.stringify(action.timer) === JSON.stringify(timer)) {
		return state
	}
	const newSlides = {...state.slides}
	newSlides[slideUid] = {
		...newSlides[slideUid],
		timer: action.timer,
	}
	return {
		...state,
		slides: newSlides,
	}
}

function handleStudentQuizChangeSlide(state, action) {
	const slides = {...state.slides}
	const slide = slides[action.slide_uid]
	//TODO change this action property set to a middleware
	action.currentIndex = slide.currentQuestionIndex
	action.newIndex = slide.currentQuestionIndex + action.direction
	slides[action.slide_uid] = {
		...slide,
		currentQuestionIndex: slide.currentQuestionIndex + action.direction,
		transitionDirection: action.direction,
		isComplete: false,
		hasChanged: slide.hasChanged,
	}
	return {
		...state,
		slides,
	}
}

function handleStudentSubmitQuizCancel(state, action) {
	const slides = {...state.slides}
	const slide = slides[action.slide_uid]
	const questions = slide.questions
	const firstEmptyQuestion = questions.findIndex(question => question.selected_answers.length === 0)
	action.newIndex = firstEmptyQuestion
	slides[action.slide_uid] = {
		...slide,
		currentQuestionIndex: firstEmptyQuestion,
		transitionDirection: -1,
		isComplete: false,
		hasChanged: true,
	}
	return {
		...state,
		slides,
	}
}

function handleStudentSubmitQuiz(state, action) {
	const slides = {...state.slides}
	const slide = slides[action.slide_uid]
	slides[action.slide_uid] = {
		...slide,
		isComplete: true,
		hasChanged: false,
	}
	return {
		...state,
		slides,
	}
}

function handleStudentSubmitQuizEdit(state) {
	const slides = {...state.slides}
	const slide = slides[state.current_slide]
	slides[state.current_slide] = {
		...slide,
		isComplete: false,
	}
	return {
		...state,
		slides,
	}
}

function studentSaveRecordedAudio(state, action) {
	// @TODO: review save max file size on story NP-13272
	const slides = {...state.slides}
	const slide = slides[state.current_slide]
	slides[state.current_slide] = {
		...slide,
		recordedAudio: action.recordedAudio,
	}
	return {
		...state,
		slides,
	}
}

function studentDeleteAudioOeqConfirmed(state) {
	const slides = {...state.slides}
	const slide = slides[state.current_slide]
	slides[state.current_slide] = {
		...slide,
		recordedAudio: null,
		audioClientUid: '',
		audioServerUid: '',
		hasChanged: true,
	}
	return {
		...state,
		slides,
	}
}

function updateStudentSubmissionAsChanged(state, action) {
	const slides = {...state.slides}
	const slide = slides[action.slide_uid]
	slides[action.slide_uid] = {
		...slide,
		hasChanged: true,
	}
	return {
		...state,
		slides,
	}
}

function handleMicPermissionsBeenRequired(state) {
	return {
		...state,
		wereMicPermissionsRequired: true,
	}
}

function handleOEQStudentRecording(state, action) {
	const slides = {...state.slides}
	const slide = slides[action.slide_uid]
	slides[action.slide_uid] = {
		...slide,
		audioClientUid: action.id,
		audioServerUid: '',
		hasChanged: true,
	}
	return {
		...state,
		slides,
	}
}

function handleOEQStudentRecordingSuccess(state, action) {
	const slides = {...state.slides}
	const slide = slides[action.slide_uid]
	if (slide.audioClientUid === action.clientUid) {
		slides[action.slide_uid] = {
			...slide,
			audioServerUid: action.uid,
		}
		return {
			...state,
			slides,
		}
	} else {
		return state
	}
}

function handleOEQStudentRecordingFailed(state, action) {
	const slides = {...state.slides}
	const slide = slides[action.slide_uid]
	if (slide.audioClientUid === action.clientUid) {
		slides[action.slide_uid] = {
			...slide,
			audioClientUid: '',
			audioServerUid: '',
		}
		return {
			...state,
			slides,
		}
	} else {
		return state
	}
}

function handleOEQStudentRecordingClear(state, action) {
	const slides = {...state.slides}
	const slide = slides[action.slide_uid]
	slides[action.slide_uid] = {
		...slide,
		audioClientUid: '',
		audioServerUid: '',
	}
	return {
		...state,
		slides,
	}
}

function handleLeadFieldChange(state, action) {
	const slides = {...state.slides}
	slides[action.slide_uid] = {
		...slides[action.slide_uid],
		[action.field]: action.value,
		isValid: true,
	}
	return {
		...state,
		slides,
	}
}

function handleLeadEnded(state, action) {
	const newState = {...state}
	if (state.current_slide === action.slide_uid) {
		newState.current_slide = null
		newState.current_status = {}
	}
	return newState
}

function handleLeadSubmitting(state, action, isSubmitting) {
	const slides = {...state.slides}
	slides[action.slide_uid] = {
		...slides[action.slide_uid],
		isSubmitting,
	}
	return {
		...state,
		slides,
	}
}

function handleLeadSubmit(state, action) {
	const newState = {...state}
	const currentSlideState = state.slides[action.slide_uid]
	if (!currentSlideState.name || currentSlideState.isSubmitting) {
		newState.slides = {
			...state.slides,
			[action.slide_uid]: {
				...state.slides[action.slide_uid],
				isValid: false,
			},
		}
		return newState
	} else {
		const newHeaders = {
			...state.headers,
			jwt: action.newJwt,
		}
		newState.headers = newHeaders
		return handleLeadSubmitting(newState, action, true)
	}
}

function initializeReadOnly(state, action) {
	let slideState
	if (!state.slides[action.slide_uid]) {
		slideState = {
			slideIndex: action.slide.slideId,
			screenName: action.slide.screenName,
			shouldShowImmersiveReader: action.slide.shouldShowImmersiveReader,
			content: action.content,
			hasChanged: true,
			type: slideTypes.SLIDE_TYPE_ID_SLIDE,
		}
	}

	return initializeSession(initializeSlide(state, action.slide_uid, slideState), action)
}

function autofillStudentName(state, action) {
	const newState = {...state}
	newState.slides[newState.current_slide] = {
		...newState.slides[newState.current_slide],
		name: action.profileData.name,
		optionalName: '',
		email: action.profileData.email,
		imageSrc: action.profileData.imageUrl,
		isValid: true,
	}
	newState.isStudentNameAutofilled = true
	return newState
}

function initializeStandaloneSessionData(state, action) {
	const decodedJwt = jwtDecode(action.jwt)
	const sessionType = parseInt(decodedJwt.extra.session_type)
	if (sessionType !== sessionTypes.EMBED) {
		return {
			...state,
			headers: action.headers,
			shouldShowImmersiveReader: action.shouldShowImmersiveReader,
		}
	}
	return state
}


const studentReducer = (state = defaultState, action) => {

	switch (action.type) {
		case actionTypes.STUDENT_INITIALIZE_POLL_WITH_DATA:
			return initializePoll(state, action)
		case actionTypes.STUDENT_INITIALIZE_QUIZ_WITH_DATA:
			return initializeQuiz(state, action)
		case actionTypes.STUDENT_INITIALIZE_OEQ_WITH_DATA:
			return initializeOeq(state, action)
		case actionTypes.MATCHING_PAIRS_STUDENT_INITIALIZE:
			return initializeMatchingPairs(state, action)
		case actionTypes.STUDENT_INITIALIZE_DRAW_IT_WITH_DATA:
			return initializeDrawIt(state, action)
		case actionTypes.STUDENT_INITIALIZE_LEAD_WITH_DATA:
			return initializeLead(state, action)
		case actionTypes.STUDENT_UPDATE_POLL_ANSWERS:
			return updatePollAnswer(state, action)
		case actionTypes.STUDENT_UPDATE_DRAW_IT_ANSWERS:
			return updateDrawItAnswer(state, action)
		case actionTypes.STUDENT_QUIZ_UPDATE_ANSWERS:
			return updateQuizAnswer(state, action)
		case actionTypes.STUDENT_UPDATE_OEQ_ANSWER:
			return updateOeqAnswer(state, action)
		case actionTypes.STUDENT_SUBMIT_OEQ_ANSWER:
		case actionTypes.STUDENT_SUBMIT_OEQ_CONFIRMED:
			return handleOeqActivitySubmission(state, action)
		case actionTypes.STUDENT_SUBMIT_POLL_CONFIRMED:
			return handlePollActivitySubmission(state, action)
		case actionTypes.STUDENT_POLL_ENDED:
		case actionTypes.STUDENT_QUIZ_ENDED:
		case actionTypes.STUDENT_OEQ_ENDED:
		case actionTypes.MATCHING_PAIRS_STUDENT_ENDED:
		case actionTypes.STUDENT_DRAW_IT_ENDED:
			return handleActivityEnded(state, action)
		case actionTypes.STUDENT_DRAW_IT_UNSUPPORTED_FILE_ERROR:
		case actionTypes.STUDENT_STATUS_UPDATED:
			return handleStudentStatusUpdated(state, action)
		case actionTypes.STUDENT_TIMER_UPDATED:
			return handleStudentTimerUpdated(state, action)
		case actionTypes.STUDENT_QUIZ_CHANGE_SLIDE:
			return handleStudentQuizChangeSlide(state, action)
		case actionTypes.STUDENT_SUBMIT_QUIZ_CANCEL:
		case actionTypes.STUDENT_SUBMIT_QUIZ_REVIEW:
			return handleStudentSubmitQuizCancel(state, action)
		case actionTypes.STUDENT_QUIZ_SUBMIT_QUIZ:
			return handleStudentSubmitQuiz(state, action)
		case actionTypes.STUDENT_SUBMIT_QUIZ_EDIT:
			return handleStudentSubmitQuizEdit(state, action)
		case actionTypes.STUDENT_DELETE_AUDIO_OEQ_CONFIRMED:
			return studentDeleteAudioOeqConfirmed(state, action)
		case actionTypes.STUDENT_SAVE_RECORDED_AUDIO_OEQ:
			return studentSaveRecordedAudio(state, action)
		case actionTypes.STUDENT_SUBMIT_FAILED:
			return updateStudentSubmissionAsChanged(state, action)
		case actionTypes.STUDENT_REQUEST_MIC_PERMISSION:
			return handleMicPermissionsBeenRequired(state)
		case actionTypes.STUDENT_OEQ_RECORDING_UPDATE:
			return handleOEQStudentRecording(state, action)
		case actionTypes.STUDENT_OEQ_RECORDING_SUBMISSION_SUCCESS:
			return handleOEQStudentRecordingSuccess(state, action)
		case actionTypes.STUDENT_OEQ_RECORDING_SUBMISSION_FAILED:
			return handleOEQStudentRecordingFailed(state, action)
		case actionTypes.STUDENT_OEQ_RECORDING_DELETE:
			return handleOEQStudentRecordingClear(state, action)
		case actionTypes.STUDENT_LEAD_FIELD_CHANGE:
			return handleLeadFieldChange(state, action)
		case actionTypes.STUDENT_LEAD_ENDED:
			return handleLeadEnded(state, action)
		case actionTypes.STUDENT_LEAD_SUBMIT:
			return handleLeadSubmit(state, action)
		case actionTypes.STUDENT_LEAD_SUBMIT_SUCCESS:
		case actionTypes.STUDENT_LEAD_SUBMIT_FAILED:
			return handleLeadSubmitting(state, action, false)
		case actionTypes.STUDENT_INITIALIZE_READ_ONLY:
			return initializeReadOnly(state, action)
		case actionTypes.STUDENT_LEAD_GOOGLE_GET_NAME_SUCCESS:
		case actionTypes.STUDENT_LEAD_OFFICE_365_GET_NAME_SUCCESS:
			return autofillStudentName(state, action)
		case actionTypes.INTERACTIVE_VIDEO_STANDALONE_INITIALIZE:
			return initializeStandaloneSessionData(state, action)
		default:
			return state
	}
}

export default studentReducer
